"use client";

import { useState, useEffect, useRef, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { TooltipPlacement } from "../../config/tours/tour.types";

interface TooltipRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourTooltipProps {
  /** Title of the current step */
  title: string;
  /** Description/body text */
  description: string;
  /** Current step number (1-indexed for display) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Preferred placement */
  placement: TooltipPlacement;
  /** Target element rect for positioning */
  targetRect: TooltipRect | null;
  /** Whether tooltip is visible */
  isVisible: boolean;
  /** Whether there's a previous step */
  hasPrevious: boolean;
  /** Whether there's a next step */
  hasNext: boolean;
  /** Handler for next button */
  onNext: () => void;
  /** Handler for previous button */
  onPrevious: () => void;
  /** Handler for skip/close */
  onSkip: () => void;
  /** Handler for complete (on last step) */
  onComplete: () => void;
}

const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_MIN_WIDTH = 280;
const TOOLTIP_MARGIN = 16;
const ARROW_SIZE = 8;

/**
 * Get responsive tooltip width based on viewport
 * Optimized for iPads and tablets
 */
function getResponsiveTooltipWidth(): number {
  if (typeof window === "undefined") return TOOLTIP_MAX_WIDTH;
  const viewportWidth = window.innerWidth;

  // For very small screens (phones), use most of the width
  if (viewportWidth < 400) {
    return Math.min(viewportWidth - TOOLTIP_MARGIN * 2, TOOLTIP_MAX_WIDTH);
  }
  // For tablets/iPads in portrait or smaller screens
  if (viewportWidth < 768) {
    return Math.min(viewportWidth - TOOLTIP_MARGIN * 3, TOOLTIP_MAX_WIDTH);
  }
  // For larger tablets and desktops
  return TOOLTIP_MAX_WIDTH;
}

/**
 * Calculate optimal tooltip position based on target rect and viewport
 * Responsive design for iPads, tablets, and all screen sizes
 */
function calculatePosition(
  targetRect: TooltipRect | null,
  placement: TooltipPlacement
): { top: number; left: number; actualPlacement: TooltipPlacement; tooltipWidth: number } {
  const tooltipWidth = getResponsiveTooltipWidth();

  if (!targetRect) {
    return { top: 0, left: 0, actualPlacement: "bottom", tooltipWidth };
  }

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;

  // Estimate tooltip height (will be refined after render)
  // Increased to 220 to better accommodate longer descriptions
  const estimatedHeight = 220;

  // Target center points (targetRect is already in viewport coordinates)
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  // Calculate available space in each direction (using viewport coordinates)
  const spaceTop = targetRect.top;
  const spaceBottom = viewportHeight - (targetRect.top + targetRect.height);
  const spaceLeft = targetRect.left;
  const spaceRight = viewportWidth - (targetRect.left + targetRect.width);

  // Auto placement logic
  let actualPlacement = placement;
  if (placement === "auto") {
    // Prefer bottom, then top, then right, then left
    if (spaceBottom >= estimatedHeight + TOOLTIP_MARGIN) {
      actualPlacement = "bottom";
    } else if (spaceTop >= estimatedHeight + TOOLTIP_MARGIN) {
      actualPlacement = "top";
    } else if (spaceRight >= tooltipWidth + TOOLTIP_MARGIN) {
      actualPlacement = "right";
    } else if (spaceLeft >= tooltipWidth + TOOLTIP_MARGIN) {
      actualPlacement = "left";
    } else {
      actualPlacement = "bottom"; // Fallback
    }
  }

  let top = 0;
  let left = 0;

  switch (actualPlacement) {
    case "top":
      top = targetRect.top - estimatedHeight - TOOLTIP_MARGIN;
      left = targetCenterX - tooltipWidth / 2;
      break;
    case "bottom":
      top = targetRect.top + targetRect.height + TOOLTIP_MARGIN;
      left = targetCenterX - tooltipWidth / 2;
      break;
    case "bottom-left":
      top = targetRect.top + targetRect.height + TOOLTIP_MARGIN;
      left = targetRect.left;
      break;
    case "bottom-right":
      top = targetRect.top + targetRect.height + TOOLTIP_MARGIN;
      left = targetRect.left + targetRect.width - tooltipWidth;
      break;
    case "top-left":
      top = targetRect.top - estimatedHeight - TOOLTIP_MARGIN;
      left = targetRect.left;
      break;
    case "top-right":
      top = targetRect.top - estimatedHeight - TOOLTIP_MARGIN;
      left = targetRect.left + targetRect.width - tooltipWidth;
      break;
    case "left":
      top = targetCenterY - estimatedHeight / 2;
      left = targetRect.left - tooltipWidth - TOOLTIP_MARGIN;
      break;
    case "right":
      top = targetCenterY - estimatedHeight / 2;
      left = targetRect.left + targetRect.width + TOOLTIP_MARGIN;
      break;
  }

  // Clamp horizontal position
  left = Math.max(TOOLTIP_MARGIN, Math.min(left, viewportWidth - tooltipWidth - TOOLTIP_MARGIN));

  // Clamp vertical position - keep within viewport (fixed positioning)
  top = Math.max(TOOLTIP_MARGIN, top);
  top = Math.min(top, viewportHeight - estimatedHeight - TOOLTIP_MARGIN);

  return { top, left, actualPlacement, tooltipWidth };
}

/**
 * TourTooltip Component
 * Displays step information with navigation controls
 */
function TourTooltipInner({
  title,
  description,
  currentStep,
  totalSteps,
  placement,
  targetRect,
  isVisible,
  hasPrevious,
  hasNext,
  onNext,
  onPrevious,
  onSkip,
  onComplete,
}: TourTooltipProps) {
  // Calculate position dynamically - updates when targetRect changes (e.g., on scroll)
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    actualPlacement: TooltipPlacement;
    tooltipWidth: number;
  } | null>(null);

  // Reset when step changes
  useEffect(() => {
    setPosition(null);
  }, [currentStep]);

  // Recalculate position whenever targetRect changes (follows element on scroll)
  useEffect(() => {
    if (targetRect) {
      const newPosition = calculatePosition(targetRect, placement);
      setPosition(newPosition);
    }
  }, [targetRect, placement]);

  // Early return if not ready
  if (!isVisible || !position) {
    return null;
  }

  const { top, left, actualPlacement, tooltipWidth } = position;

  const isLastStep = !hasNext;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: actualPlacement === "top" ? 10 : -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="fixed z-[9999] pointer-events-auto"
        style={{
          top,
          left,
          width: tooltipWidth,
          maxWidth: "calc(100vw - 32px)", // Extra safety for very small screens
        }}
      >
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary/10 to-primary/5 border-b border-gray-100">
            <span className="text-xs font-medium text-primary">
              Step {currentStep} of {totalSteps}
            </span>
            <button
              onClick={onSkip}
              className="p-1 rounded-full hover:bg-white/50 transition-colors"
              aria-label="Skip tour"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {description}
            </p>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5 mb-3">
              {Array.from({ length: totalSteps }).map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx < currentStep
                      ? "bg-primary"
                      : idx === currentStep - 1
                      ? "bg-primary"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={onSkip}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip tour
              </button>

              <div className="flex items-center gap-2">
                {hasPrevious && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onPrevious}
                    className="h-8 px-3"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}

                <Button
                  size="sm"
                  onClick={isLastStep ? onComplete : onNext}
                  className="h-8 px-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {isLastStep ? (
                    "Finish"
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Arrow pointer */}
        <div
          className={`absolute w-0 h-0 ${
            actualPlacement === "bottom"
              ? "-top-2 left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white"
              : actualPlacement === "top"
              ? "-bottom-2 left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"
              : actualPlacement === "left"
              ? "-right-2 top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-white"
              : "-left-2 top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-white"
          }`}
          style={{
            filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
}

// Memoize the component to prevent unnecessary re-renders
export const TourTooltip = memo(TourTooltipInner);

export default TourTooltip;
